import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, RefreshCw, Share2, Heart } from 'lucide-react';

// Collection of Hadiths with Albanian translation
const hadithCollection = [
  {
    arabic: "إِنَّمَا الأَعْمَالُ بِالنِّيَّاتِ",
    albanian: "Veprat vlerësohen sipas qëllimeve.",
    source: "Buhariu dhe Muslimi",
    narrator: "Omer ibn el-Hatab (r.a.)"
  },
  {
    arabic: "الْمُسْلِمُ مَنْ سَلِمَ الْمُسْلِمُونَ مِنْ لِسَانِهِ وَيَدِهِ",
    albanian: "Musliman është ai nga gjuha dhe dora e të cilit janë të sigurt muslimanët e tjerë.",
    source: "Buhariu dhe Muslimi",
    narrator: "Abdullah ibn Amr (r.a.)"
  },
  {
    arabic: "لَا يُؤْمِنُ أَحَدُكُمْ حَتَّى يُحِبَّ لِأَخِيهِ مَا يُحِبُّ لِنَفْسِهِ",
    albanian: "Askush prej jush nuk beson plotësisht derisa të dojë për vëllanë e tij atë që do për veten.",
    source: "Buhariu dhe Muslimi",
    narrator: "Enes ibn Malik (r.a.)"
  },
  {
    arabic: "مَنْ كَانَ يُؤْمِنُ بِاللَّهِ وَالْيَوْمِ الآخِرِ فَلْيَقُلْ خَيْرًا أَوْ لِيَصْمُتْ",
    albanian: "Kush beson në Allahun dhe Ditën e Fundit, le të flasë mirë ose le të heshtë.",
    source: "Buhariu dhe Muslimi",
    narrator: "Ebu Hurejra (r.a.)"
  },
  {
    arabic: "الطُّهُورُ شَطْرُ الإِيمَانِ",
    albanian: "Pastërtia është gjysma e besimit.",
    source: "Muslimi",
    narrator: "Ebu Malik el-Esh'ari (r.a.)"
  },
  {
    arabic: "الدُّنْيَا سِجْنُ الْمُؤْمِنِ وَجَنَّةُ الْكَافِرِ",
    albanian: "Dynjaja është burgu i besimtarit dhe xheneti i jobesimtarit.",
    source: "Muslimi",
    narrator: "Ebu Hurejra (r.a.)"
  },
  {
    arabic: "خَيْرُكُمْ مَنْ تَعَلَّمَ الْقُرْآنَ وَعَلَّمَهُ",
    albanian: "Më i miri prej jush është ai që mëson Kuranin dhe ia mëson të tjerëve.",
    source: "Buhariu",
    narrator: "Othman ibn Afan (r.a.)"
  },
  {
    arabic: "الْمُؤْمِنُ الْقَوِيُّ خَيْرٌ وَأَحَبُّ إِلَى اللَّهِ مِنَ الْمُؤْمِنِ الضَّعِيفِ",
    albanian: "Besimtari i fortë është më i mirë dhe më i dashur tek Allahu sesa besimtari i dobët.",
    source: "Muslimi",
    narrator: "Ebu Hurejra (r.a.)"
  },
  {
    arabic: "إِنَّ اللَّهَ جَمِيلٌ يُحِبُّ الْجَمَالَ",
    albanian: "Vërtet Allahu është i bukur dhe e do bukurinë.",
    source: "Muslimi",
    narrator: "Abdullah ibn Mesud (r.a.)"
  },
  {
    arabic: "الدُّعَاءُ هُوَ الْعِبَادَةُ",
    albanian: "Duaja është ibadet.",
    source: "Tirmidhiu",
    narrator: "Nu'man ibn Beshir (r.a.)"
  },
  {
    arabic: "أَحَبُّ الْعَمَلِ إِلَى اللَّهِ أَدْوَمُهُ وَإِنْ قَلَّ",
    albanian: "Vepra më e dashur tek Allahu është ajo që bëhet vazhdimisht, edhe nëse është e vogël.",
    source: "Buhariu dhe Muslimi",
    narrator: "Aishja (r.a.)"
  },
  {
    arabic: "تَبَسُّمُكَ فِي وَجْهِ أَخِيكَ صَدَقَةٌ",
    albanian: "Buzëqeshja jote ndaj vëllait tënd është sadaka.",
    source: "Tirmidhiu",
    narrator: "Ebu Dherr (r.a.)"
  },
  {
    arabic: "الْجَنَّةُ تَحْتَ أَقْدَامِ الأُمَّهَاتِ",
    albanian: "Xheneti është nën këmbët e nënave.",
    source: "Nesaiu",
    narrator: "Enes ibn Malik (r.a.)"
  },
  {
    arabic: "مَنْ لَا يَرْحَمُ لَا يُرْحَمُ",
    albanian: "Kush nuk mëshiron, nuk mëshirohet.",
    source: "Buhariu dhe Muslimi",
    narrator: "Xherir ibn Abdullah (r.a.)"
  },
  {
    arabic: "اتَّقِ اللَّهَ حَيْثُمَا كُنْتَ",
    albanian: "Ki frikë Allahun kudo që të jesh.",
    source: "Tirmidhiu",
    narrator: "Ebu Dherr (r.a.)"
  }
];

const DailyHadith = () => {
  const [hadith, setHadith] = useState(null);
  const [liked, setLiked] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Get daily hadith based on date
  const getDailyHadith = () => {
    const today = new Date();
    const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
    const index = dayOfYear % hadithCollection.length;
    return hadithCollection[index];
  };

  useEffect(() => {
    setHadith(getDailyHadith());
    // Check if liked
    const likedHadiths = JSON.parse(localStorage.getItem('likedHadiths') || '[]');
    const todayKey = new Date().toDateString();
    setLiked(likedHadiths.includes(todayKey));
  }, []);

  const refreshHadith = () => {
    setIsRefreshing(true);
    // Get a random hadith different from current
    const currentIndex = hadithCollection.indexOf(hadith);
    let newIndex;
    do {
      newIndex = Math.floor(Math.random() * hadithCollection.length);
    } while (newIndex === currentIndex);
    
    setTimeout(() => {
      setHadith(hadithCollection[newIndex]);
      setIsRefreshing(false);
    }, 500);
  };

  const toggleLike = () => {
    const likedHadiths = JSON.parse(localStorage.getItem('likedHadiths') || '[]');
    const todayKey = new Date().toDateString();
    
    if (liked) {
      const filtered = likedHadiths.filter(k => k !== todayKey);
      localStorage.setItem('likedHadiths', JSON.stringify(filtered));
    } else {
      likedHadiths.push(todayKey);
      localStorage.setItem('likedHadiths', JSON.stringify(likedHadiths));
    }
    setLiked(!liked);
  };

  const shareHadith = async () => {
    if (!hadith) return;
    
    const shareText = `📖 Hadithi i Ditës\n\n"${hadith.albanian}"\n\n— ${hadith.narrator}\n(${hadith.source})`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Hadithi i Ditës',
          text: shareText,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(shareText);
      alert('Hadithi u kopjua!');
    }
  };

  if (!hadith) return null;

  return (
    <section className="py-16 bg-gradient-to-br from-primary/5 to-accent/5" data-testid="daily-hadith-section">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
              <BookOpen className="w-5 h-5" />
              <span className="font-semibold">Hadithi i Ditës</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary">
              Fjalë të Arta të Profetit ﷺ
            </h2>
          </div>

          {/* Hadith Card */}
          <motion.div
            key={hadith.albanian}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl shadow-xl overflow-hidden"
          >
            {/* Arabic Text */}
            <div className="bg-gradient-to-r from-primary to-primary-600 p-8 text-center">
              <p 
                className="text-2xl md:text-3xl text-white leading-relaxed"
                style={{ fontFamily: 'Amiri, serif' }}
                dir="rtl"
              >
                {hadith.arabic}
              </p>
            </div>

            {/* Albanian Translation */}
            <div className="p-8">
              <p className="text-xl md:text-2xl text-gray-800 text-center leading-relaxed mb-6 font-medium">
                "{hadith.albanian}"
              </p>

              {/* Source */}
              <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
                <span className="bg-gray-100 px-3 py-1 rounded-full">
                  {hadith.narrator}
                </span>
                <span className="bg-accent/10 text-accent px-3 py-1 rounded-full">
                  {hadith.source}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="px-8 pb-8 flex items-center justify-center gap-4">
              <button
                onClick={toggleLike}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                  liked 
                    ? 'bg-red-100 text-red-500' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="hadith-like-btn"
              >
                <Heart className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} />
                {liked ? 'E pëlqyer' : 'Pëlqe'}
              </button>

              <button
                onClick={shareHadith}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-all"
                data-testid="hadith-share-btn"
              >
                <Share2 className="w-5 h-5" />
                Shpërndaj
              </button>

              <button
                onClick={refreshHadith}
                disabled={isRefreshing}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary-600 transition-all disabled:opacity-50"
                data-testid="hadith-refresh-btn"
              >
                <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
                Hadith tjetër
              </button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default DailyHadith;
